import React, { useEffect, useState } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';
import { useHistory } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import { fetchBrands } from '../../util/api';
import { brandsFromApiResponse } from '../../util/brands';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BrandsPage.module.css';

const defaultPageTitle = 'Nos marques | Neokasa';

export const BrandsPageComponent = props => {
  const { inProgress, error } = props;
  const [searchTerm, setSearchTerm] = React.useState('');
  const [brandsData, setBrandsData] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const history = useHistory();

  useEffect(() => {
    let cancelled = false;
    setLoadingBrands(true);
    fetchBrands()
      .then(response => {
        if (cancelled) return;
        setBrandsData(brandsFromApiResponse(response));
        setLoadError(null);
      })
      .catch(e => {
        if (cancelled) return;
        setLoadError(e?.message || 'Failed to load brands');
        setBrandsData([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingBrands(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (inProgress || loadingBrands) {
    return (
      <Page
        title={defaultPageTitle}
        description="Parcourez les marques populaires de maison connectée incluant Philips Hue, Ring, Ecobee, Aqara, et plus. Découvrez les appareils compatibles avec votre maison connectée."
        scrollingDisabled={false}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.loading}>
              <FormattedMessage id="BrandsPage.loadingBrands" />
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (error || loadError) {
    return (
      <Page
        title={defaultPageTitle}
        description="Parcourez les marques populaires de maison connectée incluant Philips Hue, Ring, Ecobee, Aqara, et plus. Découvrez les appareils compatibles avec votre maison connectée."
        scrollingDisabled={false}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.error}>
              <h2>
                <FormattedMessage id="BrandsPage.errorTitle" />
              </h2>
              <p>{(error && error.message) || loadError}</p>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const handleBrandClick = brand => {
    history.push(`/products/${brand.value}`);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const filteredBrands = brandsData.filter(brand =>
    brand.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const brandFromQuery =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('brand') : null;
  const selectedBrand = brandFromQuery && brandsData.find(b => b.value === brandFromQuery);
  const brandTitleMaybe = selectedBrand ? `${selectedBrand.brand} Neokasa` : defaultPageTitle;
  const brandDescMaybe = selectedBrand
    ? `Explorez les appareils maison connectée ${selectedBrand.brand} — compatibilité, fonctionnalités et meilleurs choix pour l'éclairage, la sécurité et l'automatisation.`
    : "Explorez les meilleures marques de maison connectée et trouvez des produits compatibles avec votre écosystème. Comparez les appareils d'éclairage, de sécurité, de climatisation et plus encore.";

  return (
    <Page title={brandTitleMaybe} description={brandDescMaybe} scrollingDisabled={false}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <div className={css.headerSection}>
              <h1 className={css.title}>Nos marques domotique et maison connectée</h1>
              <p className={css.subtitle}>Découvrez les meilleurs fabricants d'appareils connectés</p>
            </div>

            <div className={css.searchSection}>
              <div className={css.searchContainer}>
                <input
                  type="text"
                  placeholder="Rechercher des marques par nom..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={css.searchInput}
                />
                <div className={css.searchIcon}>🔍</div>
              </div>
              {searchTerm && (
                <div className={css.searchResults}>
                  {filteredBrands.length} marque{filteredBrands.length !== 1 ? 's' : ''} trouvée
                  {filteredBrands.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            <div className={css.brandsGrid}>
              {filteredBrands.map(brand => (
                <div
                  key={brand.value}
                  className={css.brandCard}
                  onClick={() => handleBrandClick(brand)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBrandClick(brand);
                    }
                  }}
                >
                  <div
                    className={
                      brand.logoOnDark
                        ? `${css.brandImageContainer} ${css.brandImageContainerDark}`
                        : css.brandImageContainer
                    }
                  >
                    {brand.logo_url ? (
                      <img
                        src={brand.logo_url}
                        alt={brand.brand}
                        className={css.brandImage}
                        loading="lazy"
                        decoding="async"
                        width="200"
                        height="72"
                        onError={e => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <div
                      className={css.brandPlaceholder}
                      style={{ display: brand.logo_url ? 'none' : 'block' }}
                    >
                      {brand.brand}
                    </div>
                  </div>
                  <h2 className={css.brandName}>{brand.brand}</h2>
                </div>
              ))}

              {searchTerm && filteredBrands.length === 0 && (
                <div className={css.noResults}>
                  <p>Aucune marque trouvée pour « {searchTerm} »</p>
                  <button onClick={() => setSearchTerm('')} className={css.clearSearchButton}>
                    Effacer la recherche
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

BrandsPageComponent.propTypes = {
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = () => {
  return {
    inProgress: false,
    error: null,
  };
};

const BrandsPage = compose(connect(mapStateToProps))(BrandsPageComponent);

export default BrandsPage;
